'use client';

import { Upload, FileText, AlertCircle, ShieldCheck } from 'lucide-react';
import { useState, useRef, useCallback } from 'react';

import { Card, CardContent } from '@/components/ui/card';

interface FileUploaderProps {
  onFileSelected: (file: File) => void;
}

export function FileUploader({ onFileSelected }: FileUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateAndSubmit = useCallback(
    (file: File) => {
      setError(null);

      if (!file.name.toLowerCase().endsWith('.xml')) {
        setError('Please upload an XML file. Supported formats: Oracle P6 XML and Microsoft Project XML.');
        return;
      }

      if (file.size > 100 * 1024 * 1024) {
        setError('File is too large. Maximum size is 100 MB.');
        return;
      }

      onFileSelected(file);
    },
    [onFileSelected],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        validateAndSubmit(files[0]);
      }
    },
    [validateAndSubmit],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        validateAndSubmit(files[0]);
      }
    },
    [validateAndSubmit],
  );

  return (
    <div className="flex flex-col items-center gap-6">
      <Card className="w-full max-w-xl mx-auto border-0 bg-transparent py-0 shadow-none">
        <CardContent className="p-0">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`flex w-full cursor-pointer flex-col items-center gap-4 rounded-lg border-2 border-dashed p-12 transition-colors ${
              isDragging
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:border-primary/50 hover:bg-muted'
            }`}
          >
            <div
              className={`flex h-14 w-14 items-center justify-center rounded-full transition-colors ${
                isDragging ? 'bg-primary/10' : 'bg-muted'
              }`}
            >
              <Upload
                className={`h-6 w-6 transition-colors ${isDragging ? 'text-primary' : 'text-muted-foreground'}`}
              />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-foreground">
                {isDragging ? 'Drop your file here' : 'Drag and drop your schedule file'}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">or click to browse your files</p>
            </div>
            <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-1.5">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground">P6 XML or MS Project XML up to 100 MB</span>
            </div>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept=".xml"
            onChange={handleFileChange}
            className="hidden"
            aria-label="Upload schedule file"
          />
        </CardContent>
      </Card>

      <div className="w-full max-w-xl mx-auto">
        <Card className="border-2 border-emerald-600/40 bg-emerald-50">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-600/15">
              <ShieldCheck className="h-6 w-6 text-emerald-700" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-bold text-emerald-900">100% Private & Secure</p>
              <p className="mt-0.5 text-xs text-emerald-800 leading-relaxed">
                Your file is analyzed in-browser and{' '}
                <span className="font-bold text-emerald-950">never uploaded or stored</span> on our servers. Everything
                stays on your device.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}
